/**
 * Actycrea: countdown.esm.js (ES6)
 * MIT
 *
 * Ticks the header countdown (days / hours / minutes / seconds) towards a
 * target date rendered by partials/_countdown.php. Pure JS timing — no GSAP
 * needed here, the values are recalculated once a second.
 */
import { PREFIX } from '../helpers/config.js';

const ATTR_ROOT   = `data-${PREFIX}-countdown`;
const ATTR_TARGET = `data-${PREFIX}-countdown-target`;

const SELECTOR_ROOT_DATA = `[${ATTR_ROOT}]`;

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = MS_PER_SECOND * 60;
const MS_PER_HOUR   = MS_PER_MINUTE * 60;
const MS_PER_DAY     = MS_PER_HOUR * 24;

export class Countdown {
  /**
   * @param {HTMLElement} element - Root element carrying `data-{PREFIX}-countdown-target`.
   */
  constructor( element ){
    if( !(element instanceof HTMLElement) ) throw new Error('Countdown: element inválido');

    this._element = element;
    this._target  = Date.parse( element.getAttribute( ATTR_TARGET ) );
    this._interval = null;

    this._fields = {
      days   : element.querySelector( '[data-countdown-days]' ) ,
      hours  : element.querySelector( '[data-countdown-hours]' ) ,
      minutes: element.querySelector( '[data-countdown-minutes]' ) ,
      seconds: element.querySelector( '[data-countdown-seconds]' )
    };

    if( Number.isNaN( this._target ) ) return;

    this._tick = this._tick.bind( this );
    this._tick( );
    this._interval = window.setInterval( this._tick , MS_PER_SECOND );
  }

  /**
   * Recalculates the remaining time and updates every field.
   *
   * @returns {void}
   */
  _tick( ){
    const remaining = Math.max( 0 , this._target - Date.now( ) );

    this._setField( 'days'    , Math.floor( remaining / MS_PER_DAY ) );
    this._setField( 'hours'   , Math.floor( ( remaining % MS_PER_DAY )    / MS_PER_HOUR ) );
    this._setField( 'minutes' , Math.floor( ( remaining % MS_PER_HOUR )   / MS_PER_MINUTE ) );
    this._setField( 'seconds' , Math.floor( ( remaining % MS_PER_MINUTE ) / MS_PER_SECOND ) );

    if( remaining <= 0 ) this.destroy( );
  }

  /**
   * @param {'days'|'hours'|'minutes'|'seconds'} key
   * @param {number} value
   * @returns {void}
   */
  _setField( key , value ){
    const field = this._fields[ key ];
    if( field ) field.textContent = String( value ).padStart( 2 , '0' );
  }

  /**
   * Stops the ticking interval.
   *
   * @returns {void}
   */
  destroy( ){
    if( this._interval === null ) return;
    window.clearInterval( this._interval );
    this._interval = null;
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ){
    return [ ...root.querySelectorAll( SELECTOR_ROOT_DATA ) ].map( ( element ) => new Countdown( element ) );
  }
}

export default Countdown;
